<!--
  A view that lays out the about form
-->
<template>
  <div>

    <form class="address_form">
      <!-- Home address subtitle -->
      <h2 class="subtitle">{{ $t(`${pagei18nKey}.homeAddressTitle`) }}</h2>

      <!-- Home address -->
      <ms-address
        ref='address'
        v-bind:address="address"
        v-bind:fiscalCode="fiscalCode"
        :askFiscalCode="true"
        v-on:update:address="updateAddress"
        v-on:update:fiscalCode="updateFiscalCode"
      ></ms-address>

      <!-- Temporary / collection / delivery address -->
      <div class="field">
        <label class="label">{{ $t(`${pagei18nKey}.differentAddress`) }}</label>
        <ms-navigation-bar
          :buttons="[ tempAddressButton, noTempAddressButton ]"
          @navigationButtonClick="tempAddressButtonClick"
        />
      </div>

      <!-- Visiting/temp address subtitle -->
      <br />
      <h2 v-if="hasTempAddress" class="subtitle">{{ $t(`${pagei18nKey}.tempAddressTitle`) }}</h2>

      <!-- Visiting address -->
      <ms-address
        ref='tempAddress'
        v-if="hasTempAddress"
        v-bind:address="tempAddress"
        v-on:update:address="updateTempAddress"
      ></ms-address>

      <!-- Date staying at visiting address until -->
      <div class="field"
      v-if="hasTempAddress">
        <label class="label">{{ $t(`${pagei18nKey}.dateTempAddress`) }}</label>
        <ms-datetime-picker
          v-model="dateAtTempAddress"
          :type="datePickerType"
          :locale="locale"
          :placeholder="$t('message.datePlaceholderText')"
          :minDatetime="minTempAddressDatetime"/>
      </div>

      <!-- Planning travel soon -->
      <div class="field">&nbsp;</div>
      <div class="field">
        <label class="label">{{ $t(`${pagei18nKey}.plannedTravel`) }}</label>
        <ms-navigation-bar
          :buttons="[ plannedTravelButton, noPlannedTravelButton ]"
          @navigationButtonClick="plannedTravelButtonClick"
        />
      </div>

      <!-- planned travel date -->
      <div class="field" v-if="plannedTravel">
        <label class="label">{{ $t(`${pagei18nKey}.plannedTravelDate`) }}</label>
        <ms-datetime-picker
          v-model="plannedTravelDate"
          :type="datePickerType"
          :locale="locale"
          :placeholder="$t('message.datePlaceholderText')"
          :minDatetime="minPlannedTravelDatetime"/>
      </div>

    </form>


    <!-- Button -->
    <!--
        We need this button wrapper to set the background color
        and styling behind the button
    -->
    <div class="address_button__container">
      <ms-navigation-bar
        :buttons="[ backButton, nextButton ]"
        @navigationButtonClick="navigationButtonClick"
      />
    </div>

    <!-- Form validation pop up -->
    <ms-app-message
      v-if="showErrorModal"
      :title="$t(`message.formErrorTitle`)"
      :isOpen.sync="showErrorModal">
      <div>{{ $t(`message.formErrorMessage`) }}</div>
    </ms-app-message>
  </div>
</template>
<script>
  import MsPostcodeInput from '@/components/ms-postcode-input'
  import AppSelect from '@/components/app-select'
  import {ButtonStates, ButtonTypes} from '@/components/app-button'
  import MsNavigationBar, {DefaultNavigationButtons} from '@/components/ms-navigation-bar'
  import MsAddress from '@/components/ms-address'
  import {Address} from '@/model/address'
  import MsDatetimePicker, {DatetimeTypes} from '@/components/ms-datetime-picker'
  import MsAppMessage from '@/components/ms-app-message'
  import { CLAIM_TYPES } from '@/model/damageClaim'

  export default {
    name: 'AddressView',

    components: {
      MsPostcodeInput,
      AppSelect,
      MsNavigationBar,
      MsAddress,
      MsDatetimePicker,
      MsAppMessage
    },

    data: function () {
      return {
        pagei18nKey: 'message.address',
        isAddressFormVisible: false,
        isTempAddressFormVisible: false,
        showErrorModal: false
      }
    },

    mounted () {

    },

    methods: {
      updateAddress (event) {
        let newAddress = new Address({})
        newAddress.address1 = event.address.address1
        newAddress.address2 = event.address.address2
        newAddress.address3 = event.address.address3
        newAddress.nameOnDoorBuzzer = event.address.nameOnDoorBuzzer
        newAddress.city = event.address.city
        newAddress.county = event.address.county
        newAddress.country = event.address.country
        newAddress.postcode = event.address.postcode
        this.isAddressFormVisible = event.formVisible
        this.$store.commit('setAddress', newAddress)
      },

      updateTempAddress (event) {
        let newAddress = new Address({})
        newAddress.address1 = event.address.address1
        newAddress.address2 = event.address.address2
        newAddress.address3 = event.address.address3
        newAddress.nameOnDoorBuzzer = event.address.nameOnDoorBuzzer
        newAddress.city = event.address.city
        newAddress.county = event.address.county
        newAddress.country = event.address.country
        newAddress.postcode = event.address.postcode
        this.isTempAddressFormVisible = event.formVisible
        this.$store.commit('setTempAddress', newAddress)
      },

      updateFiscalCode (fiscalCode) {
        this.$store.commit('setFiscalCode', fiscalCode)
      },

      /**
       * On navigation button clicked change view based on clicked button id
       */
      navigationButtonClick: function (buttonId) {
        if (buttonId === DefaultNavigationButtons.BACK.id) {
          this.$router.go(-1)
        } else if (buttonId === DefaultNavigationButtons.NEXT.id) {
          if (this.$refs.address) this.$refs.address.validate()
          if (this.$refs.tempAddress) this.$refs.tempAddress.validate()
          this.$validator.validate().then(valid => {
            if (this.isFormValid) {
              if (this.$store.getters.claimType === CLAIM_TYPES.DAMAGED) {
                let itemCount = this.$store.getters.itemCount
                if (itemCount > 0) {
                  this.$router.push('/claims-items-summary')
                } else {
                  this.$router.push('/add-claims-item')
                }
              } else if (this.$store.getters.claimType === CLAIM_TYPES.MISSING) {
                this.$router.push('/submit-claim')
              }
            } else {
              this.showErrorModal = true
            }
          })
        }
      },

      /**
       * Get the index of an object that has a matching key
       *
       * @param {string|number} value - Value to match
       * @param {Object[]} data - The data to check in
       * @param {string} key - The key to check value in each object
       */
      getIndexOfObjectFromKey: function (value, data, key) {
        let foundIndex = data.findIndex(item => {
          return item[key] === value
        })
        return foundIndex === -1 ? null : foundIndex
      },

      /**
       * Set hasTempAddress bool based on what button was clicked
       *
       * @param {string} buttonId - The id of the clicked button
       */
      tempAddressButtonClick: function (buttonId) {
        if (buttonId === this.tempAddressButton.id) {
          this.hasTempAddress = true
          // force planned travel to false
          this.plannedTravel = false
        } else if (buttonId === this.noTempAddressButton.id) {
          this.hasTempAddress = false
        }
      },

      /**
       * Set plannedTravel bool based on what button was clicked
       *
       * @param {string} buttonId - The id of the clicked button
       */
      plannedTravelButtonClick: function (buttonId) {
        if (buttonId === this.plannedTravelButton.id) {
          this.plannedTravel = true
          // force temp address to be false!
          this.hasTempAddress = false
        } else if (buttonId === this.noPlannedTravelButton.id) {
          this.plannedTravel = false
        }
      }

    },

    computed: {
      address: {
        /**
         * Gets current stored address line 1
         */
        get () {
          return this.$store.getters.address
        }
      },
      fiscalCode: {
        /**
         * Gets current stored fiscal code
         */
        get () {
          return this.$store.getters.fiscalCode
        }
      },
      tempAddress: {
        /**
         * Gets current stored address line 1
         */
        get () {
          return this.$store.getters.tempAddress
        }
      },
      hasTempAddress: {
        /**
         * Gets current hasTempAddress bool
         */
        get () {
          return this.$store.getters.hasTempAddress
        },

        /**
         * Sets hasTempAddress bool
         *
         * @param {boolean} value - Whether the user has a temp address
         */
        set (value) {
          this.$store.commit('setHasTempAddress', value)
        }
      },
      plannedTravel: {
        /**
         * Gets current plannedTravel bool
         */
        get () {
          return this.$store.getters.plannedTravel
        },

        /**
         * Sets plannedTravel bool
         *
         * @param {boolean} value - Whether the user is planning to travel soon
         */
        set (value) {
          this.$store.commit('setPlannedTravel', value)
        }
      },
      plannedTravelDate: {
        /**
         * Gets current stored planned travel date
         */
        get () {
          return this.$store.getters.plannedTravelDate
        },

        /**
         * Sets planned travel date
         *
         * @param {number} value - Epoch date of planned travel
         */
        set (value) {
          this.$store.commit('setPlannedTravelDate', value)
        }
      },
      dateAtTempAddress: {
        /**
         * Gets current stored flight date
         */
        get () {
          return this.$store.getters.dateAtTempAddreess
        },

        /**
         * Sets flight date
         *
         * @param {number} value - Epoch date of the flight
         */
        set (value) {
          this.$store.commit('setDateAtTempAddress', value)
        }
      },
      /**
       * Get datetime type
       */
      datePickerType: {
        get () {
          return DatetimeTypes.DATE
        }
      },
      /**
       * Get date locale code
       */
      locale: {
        get () {
          const currentLanguage = this.$store.getters.language
          if (
            currentLanguage &&
            currentLanguage.dateLocaleCode
          ) {
            return currentLanguage.dateLocaleCode
          }
          return null
        }
      },
      /**
       * Get back button object
       */
      backButton: {
        get () {
          return DefaultNavigationButtons.BACK
        }
      },

      /**
       * Get next button object
       */
      nextButton: {
        get () {
          // Clone DefaultNavigationButtons.NEXT and remove reference to it
          let defaultNextButton = JSON.parse(JSON.stringify(DefaultNavigationButtons.NEXT))
          let enabled = false
          if (this.isAddressFormVisible &&
          ((this.hasTempAddress && this.isTempAddressFormVisible) || !this.hasTempAddress)) {
            enabled = true
          }
          return Object.assign(
            {
              state: enabled ? ButtonStates.ENABLED : ButtonStates.DISABLED
            },
            defaultNextButton
          )
        }
      },

      /**
       * Get temporary address toggle buttons
       */
      tempAddressButton: {
        get () {
          return {
            state: ButtonStates.ENABLED,
            type: this.hasTempAddress ? ButtonTypes.PRIMARY : ButtonTypes.SECONDARY,
            text: 'message.yesButton',
            id: 'tempAddressButton'
          }
        }
      },
      /**
       * Get temporary address toggle buttons
       */
      noTempAddressButton: {
        get () {
          return {
            state: ButtonStates.ENABLED,
            type: this.hasTempAddress ? ButtonTypes.SECONDARY : ButtonTypes.PRIMARY,
            text: 'message.noButton',
            id: 'noTempAddressButton'
          }
        }
      },

      /**
       * Get planned travel toggle buttons - yes button
       */
      plannedTravelButton: {
        get () {
          return {
            state: ButtonStates.ENABLED,
            type: this.plannedTravel ? ButtonTypes.PRIMARY : ButtonTypes.SECONDARY,
            text: 'message.yesButton',
            id: 'plannedTravelButton'
          }
        }
      },

      /**
       * Get planned travel toggle buttons - no button
       */
      noPlannedTravelButton: {
        get () {
          return {
            state: ButtonStates.ENABLED,
            type: this.plannedTravel ? ButtonTypes.SECONDARY : ButtonTypes.PRIMARY,
            text: 'message.noButton',
            id: 'noPlannedTravelButton'
          }
        }
      },

      /**
       *  Gets min time user can choose for temp address date
       */
      minTempAddressDatetime: {
        get () {
          return new Date().getTime()
        }
      },

      /**
       *  Gets min time user can choose for planned travel date
       */
      minPlannedTravelDatetime: {
        get () {
          return new Date().getTime()
        }
      },

      /**
       * Checks each form field to see if the form is valid
       *
       * @returns {boolean} Whether form is valid
       */
      isFormValid () {
        let valid = Object.keys(this.fields).every(key => this.fields[key].valid)
        // check form is valid and addresses user entered are valid
        let isAddressValid = false
        if (this.$refs.address) {
          isAddressValid = this.$refs.address.isFormValid()
        }
        let isTempAddressValid = false
        if (!this.hasTempAddress) {
          isTempAddressValid = true
        } else {
          isTempAddressValid = this.$refs.tempAddress.isFormValid()
        }
        if (this.$refs.tempAddress) this.$refs.tempAddress.validate()
        return valid && isAddressValid && isTempAddressValid
      }
    }
  }
</script>

<style lang="scss">
  @import "../../assets/sass/main.scss";
  @import "./address-view.scss";
</style>
